import React from 'react';
import { Button, Chip, Checkbox } from '@heroui/react';
import { Edit, Trash2, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Glossary } from '@/services/apis/glossaries';

interface GlossaryCardProps {
  glossary: Glossary;
  onEdit: (glossary: Glossary) => void;
  onDelete: (glossary: Glossary) => void;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
}

const GlossaryCard: React.FC<GlossaryCardProps> = ({
  glossary,
  onEdit,
  onDelete,
  isSelected = false,
  onSelectionChange,
}) => {
  const { t } = useTranslation('admin');

  const englishTranslation = glossary.translations?.find((t) => t.language === 'en');
  const definition = englishTranslation?.definition || '';
  const truncatedDefinition = definition.length > 150 ? `${definition.substring(0, 150)}...` : definition;

  return (
    <div
      className={`flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow ${
        isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : 'bg-white dark:bg-gray-800'
      }`}
    >
      {onSelectionChange && (
        <div className="pt-1">
          <Checkbox
            isSelected={isSelected}
            onValueChange={onSelectionChange}
            aria-label={`Select ${glossary.name}`}
          />
        </div>
      )}

      <div className="flex-shrink-0">
        <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{glossary.name}</h3>

            {glossary.category && (
              <div className="mb-2">
                <Chip size="sm" variant="flat" color="primary">
                  {glossary.category.name}
                </Chip>
              </div>
            )}

            {definition && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{truncatedDefinition}</p>
            )}

            <div className="flex flex-wrap gap-2 mb-2">
              {glossary.aliases && glossary.aliases.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {glossary.aliases.slice(0, 3).map((alias, index) => (
                    <Chip key={index} size="sm" variant="flat" color="default">
                      {alias}
                    </Chip>
                  ))}
                  {glossary.aliases.length > 3 && (
                    <Chip size="sm" variant="flat" color="default">
                      +{glossary.aliases.length - 3}
                    </Chip>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {glossary.tags && glossary.tags.length > 0 && (
                <>
                  {glossary.tags.slice(0, 5).map((tag, index) => (
                    <Chip key={index} size="sm" variant="flat" color="secondary">
                      {tag}
                    </Chip>
                  ))}
                  {glossary.tags.length > 5 && (
                    <Chip size="sm" variant="flat" color="secondary">
                      +{glossary.tags.length - 5}
                    </Chip>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(glossary)}
              aria-label={t('glossaries.actions.edit')}
              title={t('glossaries.actions.edit')}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(glossary)}
              aria-label={t('glossaries.actions.delete')}
              title={t('glossaries.actions.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlossaryCard;

